import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  eventResolution, listTemplates, listPages, assignEventTemplate, createEventOverride,
  archivePage, type CmsPage, type CmsTemplate, type EventResolution, type ResolutionStep,
} from '../../lib/cmsAdmin';
import { ApiError } from '../../lib/apiClient';
import { useRouteBase } from '../routeBase';

/**
 * Why this event's page looks the way it does, and how to change it.
 *
 * ── RESOLUTION IS INVISIBLE UNTIL SOMETHING IS WRONG ─────────────────────────────────
 *
 * An event has no page of its own. It resolves, every render, down a chain: its own
 * override, then its assigned template, then its organization's default, then the
 * platform default, then the emergency composition in code. When the page looks unexpected
 * the question is always "which of those answered", and without this panel the only way to
 * find out is to read the database.
 *
 * So the whole chain is shown, including the steps that did NOT answer and why — "the
 * organization has a default but it has never been published" is the single most useful
 * sentence this panel can say, and reporting only the winner would never say it.
 *
 * ── ADMIN ONLY ───────────────────────────────────────────────────────────────────────
 *
 * EventManage is shared with the Organizer portal. This panel renders only inside the
 * admin shell — an organizer has no CMS console to be sent to. The engine authorizes
 * every call regardless; not rendering is a UX decision, never the security one.
 */
export function EventCmsPanel({ eventId }: { eventId: string }) {
  const base = useRouteBase();
  const [resolution, setResolution] = useState<EventResolution | null>(null);
  const [templates, setTemplates] = useState<CmsTemplate[]>([]);
  const [override, setOverride] = useState<CmsPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const found = await eventResolution(eventId);
      setResolution(found);
      const [tpl, pages] = await Promise.all([
        listTemplates({ organizationId: found.organizationId, entityKind: 'event' }),
        listPages({ pageKind: 'entity_override' }),
      ]);
      setTemplates(tpl);
      setOverride(pages.find((p) => p.entityId === eventId) ?? null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The page setup could not be read.');
    }
  }, [eventId]);

  useEffect(() => { if (base === '/admin') void load(); }, [base, load]);

  if (base !== '/admin') return null;

  async function assign(templateId: string) {
    setBusy(true); setNotice(null);
    try {
      await assignEventTemplate(eventId, templateId || null);
      setNotice(templateId
        ? 'This event now renders through that template.'
        : 'Assignment cleared. This event follows the default chain again.');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That template could not be assigned.');
    } finally { setBusy(false); }
  }

  async function makeOverride() {
    if (!window.confirm(
      'Give this event a page of its own? It stops following its template, so future '
      + 'template changes will not reach it. It starts as a copy of what it shows today.',
    )) return;
    setBusy(true); setNotice(null);
    try {
      const page = await createEventOverride(eventId, { startFrom: 'resolved' });
      setOverride(page);
      setNotice('An override was created. It shows exactly what the event shows today until you edit it.');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The override could not be created.');
    } finally { setBusy(false); }
  }

  async function dropOverride(page: CmsPage) {
    if (!window.confirm(
      'Remove this override? The event goes back to rendering through its template and '
      + 'starts receiving template changes again.',
    )) return;
    setBusy(true); setNotice(null);
    try {
      await archivePage(page.id);
      setOverride(null);
      setNotice('The override was removed. This event follows its template again.');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The override could not be removed.');
    } finally { setBusy(false); }
  }

  return (
    <section className="org-card">
      <header className="org-card__head">
        <h2 className="org-card__title">Page layout</h2>
      </header>

      {error && <p className="alert alert--error" role="alert">{error}</p>}
      {notice && <p className="alert alert--success" role="status">{notice}</p>}
      {!resolution && !error && <p className="adm-muted">Reading the page setup…</p>}

      {resolution && (
        <>
          <p className="cms-resolved">
            This event renders from <strong>{describeStep(resolution.resolvedFrom)}</strong>
            {resolution.version != null && <> (v{resolution.version})</>}.
          </p>

          <ol className="cms-chain">
            {resolution.candidates.map((c) => (
              <li key={c.step}
                className={`cms-chain__step${c.step === resolution.resolvedFrom ? ' is-winner' : ''}`}>
                <span className="cms-chain__name">{describeStep(c.step)}</span>
                <span className="cms-chain__state">{describeState(c)}</span>
              </li>
            ))}
          </ol>

          <div className="cms-chain__controls">
            <label className="field">
              <span className="field__label">Template</span>
              <select
                className="field__input"
                value={resolution.assignedTemplateId ?? ''}
                disabled={busy || Boolean(override)}
                onChange={(e) => { void assign(e.target.value); }}
              >
                <option value="">No template chosen — follow the default</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}{t.isDefault ? ' (default)' : ''}
                    {t.scope === 'platform' ? ' · platform' : ''}
                    {t.publishedVersion ? '' : ' · never published'}
                  </option>
                ))}
              </select>
              {override && (
                <span className="field__help">
                  This event has its own page, so the template is not consulted. Remove the
                  override to use a template again.
                </span>
              )}
            </label>

            <div className="adm-actions">
              {override
                ? (
                  <>
                    <Link className="button button--secondary" to={`/admin/content/pages/${override.id}`}>
                      Edit this event&rsquo;s page
                    </Link>
                    <button type="button" className="button button--ghost" disabled={busy}
                      onClick={() => { void dropOverride(override); }}>
                      Remove the override
                    </button>
                  </>
                )
                : (
                  <button type="button" className="button button--ghost" disabled={busy}
                    onClick={() => { void makeOverride(); }}>
                    Give this event its own page
                  </button>
                )}
              {resolution.assignedTemplateId && !override && (
                <Link className="button button--ghost"
                  to={`/admin/content/templates/${resolution.assignedTemplateId}?preview=${eventId}`}>
                  Edit the template
                </Link>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function describeStep(step: ResolutionStep['step']): string {
  switch (step) {
    case 'page_override': return 'this event’s own page';
    case 'assigned_template': return 'the template assigned to it';
    case 'organization_default': return 'the organization default template';
    case 'platform_default': return 'the platform default template';
    default: return 'the built-in fallback page';
  }
}

/** Why a step did or did not answer. The unpublished-default case is the useful one. */
function describeState(c: ResolutionStep): string {
  if (c.step === 'hardcoded_fallback') return 'always available';
  if (!c.exists) return 'none';
  if (!c.published) return 'exists, never published — so it is skipped';
  return c.name ? `${c.name}, published` : 'published';
}
